import { BottomRight, TopLeft } from "@dojo/framework/core/meta/Dimensions";
import html from "csi/html";
import $ from "jquery";
import * as _ from "lodash";
import {
	CANVAS_EDITOR_ROOT,
	COLOR_KEY,
	CURVE_SNAP_OFFSET,
	DEFAULT_COLOR,
	DEFAULT_FONT_FAMILY,
	DEFAULT_FONT_SIZE,
	DEFAULT_TEXTBOX_PADDING,
	EXT_OFFSET,
	FROALA_FORMAT_STYLES,
	GRID_INC,
	INDENT_SIZE,
	MIN_DIMENSION,
	MIN_RIGHT_DISTANCE_FOR_INDENT,
	PADDING_INSIDE_ATTRIBUTE_PLACEHOLDER_SHAPES,
	PLACEHOLDER_DISABLED_SPAN,
	PLACEHOLDER_LABEL_SPAN, PLACEHOLDER_RICH_TEXT_DIV, PLACEHOLDER_SEPARATOR_SPAN,
	PLACEHOLDER_VALUE_SPAN,
	SPACE_BETWEEN_ATTRIBUTE_PLACEHOLDER_SHAPES, TEXTALIGN_KEY, TEXT_STYLE_DEFAULTS, VERTICALALIGN_KEY
} from "../Constants";
import {
	calculateAdjustGroup,
	getShapeCenter,
	getTextDimensions,
	IRectSizeBox,
	mapPointToShapeMatrix
} from "../store/reducers/util";
import { IAnyObjectType } from "./components/SelectionBox";
import { ICustomShapePoint } from "./interfaces/model/dataStructures/CustomShapePoint";
import { IGeoBoundaryMeta } from "./interfaces/model/dataStructures/GeoBoundaryMeta";
import { FillTypes } from "./interfaces/model/enumerations/FillTypes";
import { AutosizeMode, GeometryTypes, IPageObjectTypes, PathOperationTypes } from "./interfaces/model/enumerations/index";
import { PlaceholderTextFormat } from "./interfaces/model/enumerations/PlaceholderTextFormat";
import { HandleType, ICustomGeometry, IHandlePoint, IPenGeometry } from "./interfaces/model/geometry/CustomGeometry";
import {
	ICloseShapePath,
	IDrawCubicBezierCurveTo,
	IDrawLineTo,
	IDrawQuadraticBezierCurveTo,
	IMovePathTo, IShapePath, PathOperation
} from "./interfaces/model/geometry/ShapePath";
import { IShapePositionCoordinate } from "./interfaces/model/geometry/ShapePositionCoordinate";
import { ITextStyles } from "./interfaces/model/index";
import { IConnectionShape, IGroupShape, IPicture, IShape } from "./interfaces/model/PageObjects/index";
import { ITransform2D } from "./interfaces/model/properties";
import { IFillProperties, ISolidFillProperties } from "./interfaces/model/properties/FillProperties";
import { ISnapSvg } from "./interfaces/model/SnapSvg";
import { PageObjectType } from "./interfaces/model/Types";
import { IPosition } from "./meta/Drag/CsiDragController";
import { getCustomGeometry, getZoomValue } from "./selectors/index";

declare const Snap: ISnapSvg;

export function isShapeObject(value: PageObjectType): value is IShape {
	return value.type === IPageObjectTypes.SHAPE;
}

export function isGroupShapeObject(value: PageObjectType): value is IGroupShape {
	return value.type === IPageObjectTypes.GROUP_SHAPE;
}

export function isConnectionShapeObject(value: PageObjectType): value is IConnectionShape {
	return value.type === IPageObjectTypes.CONNECTION_SHAPE;
}

export function isPictureObject(value: PageObjectType): value is IPicture {
	return value.type === IPageObjectTypes.PICTURE;
}

/**
 * Function to get maximum visible dimension.
 * @param {actualDimention}
 * @returns {maximumDimension}
 */
export const getMaxDimension = (actualDimention: number) => {
	return Math.max(actualDimention, MIN_DIMENSION);
};

export const getRadians = (p1: IPosition, p2: IPosition) => {
	let r = Math.atan2(p2.y - p1.y, p2.x - p1.x);
	r = (r < 0) ? 2 * Math.PI + r : r;
	return r; // (r - (1.5 * Math.PI));
};

export const getDistance = (p1: IPosition, p2: IPosition) => {
	return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
};

export const getSnapValue = (n: number) => {
	// summary: snap number to grid
	return Math.round(n / GRID_INC) * GRID_INC;
};

/**
 * Function to get default geometry boundary meta of pageObject.
 * @param transforms ITransform2D object of pageObject
 * @returns default pageObject geometry boundary meta
 */
export const getGeoBoundaryMeta = (transforms: ITransform2D): IGeoBoundaryMeta => {
	const { cx, cy } = transforms.ext;
	const { x, y } = transforms.off;
	return {
		height: cy,
		width: cx,
		xMax: x + cx,
		xMin: x,
		yMax: y + cy,
		yMin: y,
	};
};

/**
 * Update the point as per delta X,Y and scale point.
 * @param {IShapePositionCoordinate} pt Point to scale.
 * @param {number} dx Delta X.
 * @param {number} dy Delta Y.
 */
export const updateShapePathPosition = (pt: IShapePositionCoordinate, dx: number, dy: number) => {
	pt.x = pt.x as number + dx;
	pt.y = pt.y as number + dy;
};

/**
 * Given two points, rotate the first point such that it is at a multiple
 * of 45 degrees around the second point, snapping to the line that's closest.
 * @param {IPosition} pt First point, to be rotated about the second
 * @param {IPosition} center point to rotate around
 */
export const calculateAngleSnappedPoint = (pt: IPosition, center: IPosition) => {
	const angle = Math.atan2(pt.y - center.y, pt.x - center.x);

	let closestAngle = 0;
	let closestDistance = Infinity;

	for (let i = -Math.PI; i <= 2 * Math.PI; i = i + Math.PI / 4) {
		const absDistance = Math.abs(angle - i);
		if (absDistance < closestDistance) {
			closestAngle = i;
			closestDistance = absDistance;
		}
	}

	const totalDistance = getDistance(pt, center);

	const xValue = center.x + Math.cos(closestAngle) * totalDistance;
	const yValue = center.y + Math.sin(closestAngle) * totalDistance;

	return { x: xValue, y: yValue } as IPosition;
};

/**
 * Update pathlist, for each path list & operation update point location and scale as per computation basis.
 */
export const updateShapePathList = (pathList: IShapePath[], dx: number, dy: number) => {
	_.forEach(pathList, (shapePath: IShapePath) => {
		_.forEach(shapePath.operations, (operation: PathOperation) => {
			switch (operation.type) {
				case PathOperationTypes.ARC_TO:
					break;
				case PathOperationTypes.CLOSE:
					break;
				case PathOperationTypes.LINE_TO:
					const lnTo = operation as IDrawLineTo;
					updateShapePathPosition(lnTo.pt, dx, dy);
					break;
				case PathOperationTypes.MOVE_TO:
					const mvTo = operation as IMovePathTo;
					updateShapePathPosition(mvTo.pt, dx, dy);
					break;
				case PathOperationTypes.CUBIT_BEZ_TO:
					const cubic = operation as IDrawCubicBezierCurveTo;
					_.forEach(cubic.pts, (pt: IShapePositionCoordinate) => {
						updateShapePathPosition(pt, dx, dy);
					});
					break;
				case PathOperationTypes.QUAD_BEZ_TO:
					const quad = operation as IDrawQuadraticBezierCurveTo;
					_.forEach(quad.pts, (pt: IShapePositionCoordinate) => {
						updateShapePathPosition(pt, dx, dy);
					});
					break;
				default:
					break;
			}
		});
	});
};
/**
 * Update the point as per delta X,Y and scale point.
 * @param {IShapePositionCoordinate} pt Point to scale.
 * @param {number} dx value in Percentage.
 * @param {number} dy value in Percentage.
 */
export const updateShapePathPositionPer = (
	pt: IShapePositionCoordinate,
	dWidth: number, dHeight: number, isFlipped?: boolean,
) => {
	if (typeof (pt.x) === "number" && typeof (pt.y) === "number") {
		const oldPointX = pt.x;
		const oldPointY = pt.y;
		pt.x = pt.x + dWidth * (pt.x);
		pt.y = pt.y + dHeight * (pt.y);
		if (isFlipped) {
			// keep the each point sign same as it was before flipped.
			pt.x = pt.x * (((pt.x < 0 && oldPointX > 0) || (pt.x > 0 && oldPointX < 0)) ? -1 : 1);
			pt.y = pt.y * (((pt.y < 0 && oldPointY > 0) || (pt.y > 0 && oldPointY < 0)) ? -1 : 1);
		}
	}
};
/**
 * Update pathlist, for each path list & operation update point location and scale as per computation basis.
 */
export const updateShapePathListPercentage = (
	pathList: IShapePath[],
	dWidth: number, dHeight: number, isFlipped?: boolean, handlePoints?: IHandlePoint[],
) => {
	_.forEach(pathList, (shapePath: IShapePath) => {
		_.forEach(shapePath.operations, (operation: PathOperation) => {
			switch (operation.type) {
				case PathOperationTypes.ARC_TO:
					break;
				case PathOperationTypes.CLOSE:
					break;
				case PathOperationTypes.LINE_TO:
					const lnTo = operation as IDrawLineTo;
					updateShapePathPositionPer(lnTo.pt, dWidth, dHeight, isFlipped);
					break;
				case PathOperationTypes.MOVE_TO:
					const mvTo = operation as IMovePathTo;
					updateShapePathPositionPer(mvTo.pt, dWidth, dHeight, isFlipped);
					break;
				case PathOperationTypes.CUBIT_BEZ_TO:
					const cubic = operation as IDrawCubicBezierCurveTo;
					_.forEach(cubic.pts, (pt: IShapePositionCoordinate) => {
						updateShapePathPositionPer(pt, dWidth, dHeight, isFlipped);
					});
					break;
				case PathOperationTypes.QUAD_BEZ_TO:
					const quad = operation as IDrawQuadraticBezierCurveTo;
					_.forEach(quad.pts, (pt: IShapePositionCoordinate) => {
						updateShapePathPositionPer(pt, dWidth, dHeight, isFlipped);
					});
					break;
				default:
					break;
			}
		});
	});
	if (handlePoints) {
		_.forEach(handlePoints, (handlePoint: IHandlePoint) => {
			if (handlePoint.frontHandle) {
				updateShapePathPositionPer(handlePoint.frontHandle, dWidth, dHeight, isFlipped);
			}
			if (handlePoint.backHandle) {
				updateShapePathPositionPer(handlePoint.backHandle, dWidth, dHeight, isFlipped);
			}
		});
	}
};

/**
 * Get the control point based on 3 surrounding points.
 *
 * @param start {IPosition} start position for the curve.
 * @param center {IPosition} center position for the curve.
 * @param end {IPosition} end position for the curve.
 * @param dir {number} Direction for the point.
 */
const _getControlPoint = (
	start: ICustomShapePoint,
	center: ICustomShapePoint,
	end: ICustomShapePoint, dir: number,
) => {
	const dist = getDistance(start.position as IPosition, end.position as IPosition) / 6;
	const rad = getRadians(start.position as IPosition, end.position as IPosition);

	const centerPosition = center.position as IPosition;
	return {
		x: centerPosition.x + dir * getSnapValue(dist * Math.cos(rad)),
		y: centerPosition.y + dir * getSnapValue(dist * Math.sin(rad)),
	} as IShapePositionCoordinate;
};

/**
 * Gets the value of a handle.  Passed whether to take the front or back handle, 
 * and what position to default to if the handle is undefined (usually the point
 * the handle is attached to).
 */
export const getHandleValue = (handle: IHandlePoint, front: boolean, def: IPosition) => {
	if (!handle || handle.type === HandleType.STRAIGHT) {
		return _.cloneDeep(def);
	}
	const handlePosition = front ? handle.frontHandle : handle.backHandle;
	return handlePosition ? handlePosition : _.cloneDeep(def);
};

/**
 * Generates entirely new paths for a shape based on the shape's points and handle points
 */
export const generatePenOperationList = (pts: ICustomShapePoint[], handlePts: IHandlePoint[], isClosed: boolean) => {
	// if last point is a close operation, remove it
	if (!pts[pts.length - 1].position) {
		pts.splice(pts.length - 1, 1);
	}

	const pathOperations: PathOperation[] = [];
	const n = pts.length;
	if (n === 1) {
		return [];
	}

	const p1 = _.cloneDeep(pts[0]);

	pathOperations.push({
		pt: p1.position,
		type: PathOperationTypes.MOVE_TO,
	} as IMovePathTo);

	for (let i = 1; i < n; i++) {
		const firstControlPoint = _.cloneDeep(getHandleValue(handlePts[i - 1], true, pts[i - 1].position as IPosition));
		const secondControlPoint = _.cloneDeep(getHandleValue(handlePts[i], false, pts[i].position as IPosition));

		pathOperations.push({
			pts: [firstControlPoint, secondControlPoint, pts[i].position as IShapePositionCoordinate],
			type: PathOperationTypes.CUBIT_BEZ_TO,
		});
	}
	if (isClosed) {
		const firstControlPoint = _.cloneDeep(getHandleValue(handlePts[handlePts.length - 1], true, pts[handlePts.length - 1].position as IPosition));
		const secondControlPoint = _.cloneDeep(getHandleValue(handlePts[0], false, p1.position as IPosition));
		pathOperations.push({
			pts: [firstControlPoint, secondControlPoint, _.cloneDeep(p1.position) as IShapePositionCoordinate],
			type: PathOperationTypes.CUBIT_BEZ_TO,
		});
		pathOperations.push({
			type: PathOperationTypes.CLOSE,
		});
	}
	return pathOperations;
};

/**
 * Generates a new set of handles for a point that has been double-clicked.  Attempts to create
 * a smooth curve between them.
 */
export const generateNewHandles = (prevPoint: IPosition, point: IPosition, nextPoint: IPosition) => {
	const firstDistance = getDistance(prevPoint, point);
	const secondDistance = getDistance(nextPoint, point);
	const frontToBackRatio = secondDistance / firstDistance;
	const distanceFactor = 1 / 2.5;
	const frontHandle = { x: 0, y: 0 } as IPosition;
	const backHandle = { x: 0, y: 0 } as IPosition;

	const flipHandle = (handle: IPosition, targetPoint: IPosition, newX: number, newY: number) => {
		let x = newX;
		let y = newY;
		if (handle.x > point.x && targetPoint.x < point.x ||
			handle.x < point.x && targetPoint.x > point.x) {
			handle.x = point.x - (handle.x - point.x);
			x = -x;
		}
		if (handle.y > point.y && targetPoint.y < point.y ||
			handle.y < point.y && targetPoint.y > point.y) {
			handle.y = point.y - (handle.y - point.y);
			y = -y;
		}

		return { x, y };
	};

	if (firstDistance > secondDistance) {
		const targetPoint = _.cloneDeep(nextPoint);
		const secondTargetPoint = _.cloneDeep(point);
		secondTargetPoint.x += point.x - prevPoint.x;
		secondTargetPoint.y += point.y - prevPoint.y;
		targetPoint.x = targetPoint.x * (1 - frontToBackRatio) + secondTargetPoint.x * frontToBackRatio;
		targetPoint.y = targetPoint.y * (1 - frontToBackRatio) + secondTargetPoint.y * frontToBackRatio;
		let angle = 0;
		if (targetPoint.x == point.x) {
			if (targetPoint.y > point.y) {
				angle = 0;
			} else {
				angle = Math.PI;
			}
		} else {
			const slope = (targetPoint.y - point.y) / (targetPoint.x - point.x);
			angle = Math.atan(slope);
		}
		const newX = Math.cos(angle) * secondDistance * distanceFactor;
		const newY = Math.sin(angle) * secondDistance * distanceFactor;

		frontHandle.x = point.x + newX;
		frontHandle.y = point.y + newY;

		const { x, y } = flipHandle(frontHandle, nextPoint, newX, newY);

		backHandle.x = point.x - x / frontToBackRatio;
		backHandle.y = point.y - y / frontToBackRatio;
	} else {
		const targetPoint = _.cloneDeep(prevPoint);
		const secondTargetPoint = _.cloneDeep(point);
		secondTargetPoint.x += point.x - nextPoint.x;
		secondTargetPoint.y += point.y - nextPoint.y;
		targetPoint.x = targetPoint.x * (1 - 1 / frontToBackRatio) + secondTargetPoint.x / frontToBackRatio;
		targetPoint.y = targetPoint.y * (1 - 1 / frontToBackRatio) + secondTargetPoint.y / frontToBackRatio;
		let angle = 0;
		if (targetPoint.x == point.x) {
			if (targetPoint.y > point.y) {
				angle = 0;
			} else {
				angle = Math.PI;
			}
		} else {
			const slope = (targetPoint.y - point.y) / (targetPoint.x - point.x);
			angle = Math.atan(slope);
		}
		const newX = Math.cos(angle) * firstDistance * distanceFactor;
		const newY = Math.sin(angle) * firstDistance * distanceFactor;

		backHandle.x = point.x - newX;
		backHandle.y = point.y - newY;

		const { x, y } = flipHandle(backHandle, prevPoint, newX, newY);

		frontHandle.x = point.x + x * frontToBackRatio;
		frontHandle.y = point.y + y * frontToBackRatio;
	}
	return {
		backHandle,
		frontHandle,
		frontToBackRatio,
		type: HandleType.MIRRORED,
	} as IHandlePoint;
};

/**
 * Build shape path using shapePoints.
 * In case 2 points create line.
 * If points > 2 then, create using bezier curve.
 * @param {ICustomShapePoint[]} pts
 * @param {boolean} isLastPoint - whether the point that has been moved is the last point
 * @param {boolean} forceClose
 * @param {boolean} lineShape
 * @return {PathOperation[]}
 */
export const generateShapePathList = (
	pts: ICustomShapePoint[], isLastPoint?: boolean,
	forceClose?: boolean, lineShape?: boolean,
) => {
	// if last point is a close operation, remove it
	if (!pts[pts.length - 1].position) {
		pts.splice(pts.length - 1, 1);
	}

	let bClose = true;
	const pathOperations: PathOperation[] = [];
	const n = pts.length;
	if (n === 1) {
		return [];
	}

	if (forceClose) {
		if (isLastPoint) {
			pts[n - 1] = _.cloneDeep(pts[0]);
		} else {
			pts[0] = _.cloneDeep(pts[n - 1]);
		}
	}

	const pn = pts[n - 1];
	let p1 = pts[0];
	let p2 = pts[1];
	let p0: ICustomShapePoint | null = null;
	let p3: ICustomShapePoint | null = null;

	pathOperations.push({
		pt: p1.position,
		type: PathOperationTypes.MOVE_TO,
	} as IMovePathTo);

	if (n === 2) {
		pathOperations.push({
			pt: p2.position,
			type: PathOperationTypes.LINE_TO,
		} as IDrawLineTo);
	} else {
		if (bClose && equals(p1, pn)) {
			p0 = pts[n - 2];
			if (isLastPoint) {
				pts[n - 1] = _.cloneDeep(p1);
			} else {
				pts[0] = _.cloneDeep(pts[n - 1]);
			}
		} else {
			bClose = false;
		}

		for (let i = 1; i < n; ++i) {
			if (i + 1 === n) {
				if (bClose) {
					p3 = pts[1];
				} else {
					p3 = null;
				}
			} else {
				p3 = pts[i + 1];
			}

			if (lineShape && pts) {
				pathOperations.push({
					pt: pts[i].position,
					type: PathOperationTypes.LINE_TO,
				} as IDrawLineTo);
			} else if (p0 && p3) {
				const c1 = _getControlPoint(p0, p1, p2, 1);
				const c2 = _getControlPoint(p1, p2, p3, -1);
				const operation: IDrawCubicBezierCurveTo = {
					pts: [c1, c2, p2.position as IShapePositionCoordinate],
					type: PathOperationTypes.CUBIT_BEZ_TO,
				};
				pathOperations.push(operation);
			} else if (p0) {
				const c1 = _getControlPoint(p0, p1, p2, 1);
				const operation: IDrawQuadraticBezierCurveTo = {
					pts: [c1, p2.position as IShapePositionCoordinate],
					type: PathOperationTypes.QUAD_BEZ_TO,
				};
				pathOperations.push(operation);
			} else if (p3) {
				const c1 = _getControlPoint(p1, p2, p3, -1);
				const operation: IDrawQuadraticBezierCurveTo = {
					pts: [c1, p2.position as IShapePositionCoordinate],
					type: PathOperationTypes.QUAD_BEZ_TO,
				};
				pathOperations.push(operation);
			}

			p0 = p1;
			p1 = p2;
			if (p3) {
				p2 = p3;
			}
		}
	}
	// Add close point in path operations
	if (bClose && pts.length > 2) {
		pathOperations.push({ type: PathOperationTypes.CLOSE } as ICloseShapePath);
	}
	return pathOperations;
};

/**
 * Check for two offset equality
 * @param {any} p1
 * @param {any} p2
 * @return {boolean}
 */
export const equals = (p1: any, p2: any) => {
	return (Math.abs(p1.position.x - p2.position.x) <= CURVE_SNAP_OFFSET) &&
		(Math.abs(p1.position.y - p2.position.y) <= CURVE_SNAP_OFFSET);
};

/**
 * Returns path ebd points
 * @param {PathOperation} pathOperation
 */
export const getPathEndPoint = (pathOperation: PathOperation) => {
	let pt: IShapePositionCoordinate | undefined = undefined;
	switch (pathOperation.type) {
		case PathOperationTypes.ARC_TO:
			break;
		case PathOperationTypes.CLOSE:
			break;
		case PathOperationTypes.LINE_TO:
			const lnTo = pathOperation as IDrawLineTo;
			pt = lnTo.pt;
			break;
		case PathOperationTypes.MOVE_TO:
			const mvTo = pathOperation as IMovePathTo;
			pt = mvTo.pt;
			break;
		case PathOperationTypes.CUBIT_BEZ_TO:
			const cubic = pathOperation as IDrawCubicBezierCurveTo;
			// use last point as marker point.
			pt = cubic.pts[2];
			break;
		case PathOperationTypes.QUAD_BEZ_TO:
			const quad = pathOperation as IDrawQuadraticBezierCurveTo;
			pt = quad.pts[1];
			break;
		default:
			break;
	}
	return pt;
};

/**
 * Returns true if the object has bezier curves
 * @param {path} a shape path to check
 */
export const hasBezierCurves = (path: IShapePath) => {
	for (const op of path.operations) {
		if (op.type === PathOperationTypes.CUBIT_BEZ_TO) {
			return true;
		}
	}
	return false;
};

/**
 * Generate html for default styles as per froala format
 * @param defaultStyles {ITextStyles}
 * @return {any}
 */
export const getDefaultFroalaHtml = (defaultStyles: ITextStyles): any => {
	if (!defaultStyles) {
		return { outerHTML: "" };
	}
	const textElement = document.createElement("p");
	textElement.style.textAlign = defaultStyles["text-align"];
	let child: any;
	// Create dom for styles line strong, underline, strikethrough and italic
	// <strong><u>...</u></strong>
	_.each(defaultStyles, (value, key, object) => {
		if (FROALA_FORMAT_STYLES.indexOf(value) !== -1) {
			child = document.createElement(value);
			if (textElement.lastElementChild) {
				const nChild = textElement.removeChild(textElement.lastElementChild);
				child.appendChild(nChild);
			} else {
				child.innerHTML = "&#8203;";
			}
			textElement.appendChild(child);
		}
	});

	const span = document.createElement("span");
	// adding span level styles
	span.style.fontFamily = defaultStyles["font-family"];
	span.style.fontSize = defaultStyles["font-size"];
	span.style.fontStyle = defaultStyles["font-style"];
	span.style.fontWeight = defaultStyles["font-weight"];
	span.style.color = hexToRgb(defaultStyles.color || TEXT_STYLE_DEFAULTS[COLOR_KEY]);

	if (textElement.firstChild) {
		span.appendChild(textElement.firstChild as Node);
	} else {
		span.innerHTML = "&#8203;";
	}
	let headingElement;
	let listElement;
	// check if heading style is in defaults
	if (defaultStyles.paragraph !== "N") {
		headingElement = document.createElement(defaultStyles.paragraph);
		headingElement.style.textAlign = defaultStyles["text-align"];
		headingElement.appendChild(span);
	}
	// check if heading and OL or UL style is in defaults
	if ((defaultStyles.OL || defaultStyles.UL)) {
		listElement = document.createElement((defaultStyles.OL || defaultStyles.UL).toLowerCase());
		listElement.appendChild(document.createElement("li"));
	}
	// check if heading style and OR or UL both are present
	if ((defaultStyles.OL || defaultStyles.UL)
		&& defaultStyles.paragraph !== "N" && listElement && listElement.lastChild) {
		listElement.lastChild.appendChild(headingElement as HTMLElement);
	}
	if (!headingElement && !listElement) {
		textElement.appendChild(span);
	}
	return listElement || headingElement || textElement;
};

/**
 * Return the post 7.2 HTML content of default placeholder using display name
 * @param displayName - display name for text, usually derived through attribute key via getDisplayNameForKey
 */
export const getDefaultTextPlaceholderHTML = (displayName: string) => {
	return {
		content: "<p style=\"text-align: left;\">" +
			`<span id="${PLACEHOLDER_LABEL_SPAN}"` +
			`style="font-family: ${DEFAULT_FONT_FAMILY}; font-size: ${DEFAULT_FONT_SIZE}px; color: ${DEFAULT_COLOR};"` +
			`>${displayName}:</span>` +
			`<span id="${PLACEHOLDER_DISABLED_SPAN}"` +
			`style="display: none; font-size: ${DEFAULT_FONT_SIZE}px; color: var(--Grey700)"` +
			`><em>${displayName}:</em></span>` +
			`<span id="${PLACEHOLDER_SEPARATOR_SPAN}"> </span>` +
			`<span id="${PLACEHOLDER_VALUE_SPAN}"` +
			`style="font-family: ${DEFAULT_FONT_FAMILY}; font-size: ${DEFAULT_FONT_SIZE}px; color: ${DEFAULT_COLOR};"` +
			">{Value}</span>" +
			"</p>",
	};
};

/**
 * Clear text style formatting on shape text
 * @param domString {string}
 * @return {string}
 */
export const clearTextFormatting = (domString: string): string => {
	const contentElement: HTMLElement = document.createElement("div") as HTMLElement;
	contentElement.innerHTML = domString;
	let paragraphList: string[] = [];
	while (contentElement.firstChild) {
		const childText = _clearFormatting(contentElement.firstChild as HTMLElement);
		if (childText) {
			paragraphList = paragraphList.concat(childText);
		}
		contentElement.removeChild(contentElement.firstChild);
	}
	_.each(paragraphList, (pText) => {
		const pElement: HTMLElement = document.createElement("p") as HTMLElement;
		const span = document.createElement("span");
		pElement.appendChild(span);
		span.innerText = pText;
		contentElement.appendChild(pElement);
	});
	return contentElement.innerHTML;
};

/**
 * Clear placeholder text formatting
 * @param domString - domString to clear formatting from
 * @param placeholderTextFormat - indicate which placeholder text span are we clearing from
 */
export const clearPlaceholderTextFormatting = (domString: string, placeholderTextFormat: string): string => {
	const div: HTMLDivElement = document.createElement("div");
	div.innerHTML = domString;
	if (placeholderTextFormat !== PlaceholderTextFormat.LABEL) {
		const valueSpan: HTMLSpanElement = div.querySelector(`#${PLACEHOLDER_VALUE_SPAN}`);
		valueSpan.removeAttribute("style");
		valueSpan.innerHTML = valueSpan.innerText;
	}
	if (placeholderTextFormat !== PlaceholderTextFormat.VALUE) {
		const labelSpan: HTMLSpanElement = div.querySelector(`#${PLACEHOLDER_LABEL_SPAN}`);

		// preserve display when removing the style, since labels could be off or on (hidden or visible)
		const displayStyle = labelSpan.style.display;
		labelSpan.removeAttribute("style");
		labelSpan.style.display = displayStyle;

		// clear the innerHTML which could consist of u, em, strong, etc
		labelSpan.innerHTML = labelSpan.innerText;
	}
	return div.innerHTML;
};

/**
 * Clear text formatting
 * @param domElement {HTMLElement}
 * @return {string[] | null}
 */
const _clearFormatting = (domElement: HTMLElement): string[] | null => {
	const blockTags = ["H1", "H2", "P", "H3", "H4", "LI"];
	const parentBlockTags = ["OL", "UL"];
	if (blockTags.indexOf(domElement.nodeName) > -1) {
		return [domElement.innerText];
	}
	if (parentBlockTags.indexOf(domElement.nodeName) > -1) {
		let childP: string[] = [];
		_.each(domElement.children, (child) => {
			const childT = _clearFormatting(child as HTMLElement);
			if (childT) {
				childP = childP.concat(childT);
			}
		});
		return childP;
	}
	return null;
};

/**
 * This will prepare the HTML if needed for placeholders after C8-16890 7.2
 * HTML Structure with placeholder showing as "Colorway: Arnold Rouge" is as follows:
 * <p>
 *     <span id="csi-placeholder-label-span">Colorway:</span>
 *     <span id="csi-placeholder-disabled-span">Colorway:</span>
 *     <span id="csi-placeholder-separator-span"> </span>
 *     <span id="csi-placeholder-value-span">Arnold Rouge</span>
 * </p>
 * If all parts of the span were visible at once:
 * -----------------------------------
 * | Colorway:Colorway: Arnold Rouge |
 * -----------------------------------
 *  ^--------^ Label Span
 *            ^-------^ Disabled Span
 *                     ^ Separator Span
 *                      ^-----------^ Value Span
 * @param {IShape} pageObject
 */
export const setupPlaceholderHTML = (pageObject: IShape) => {
	const pageObjectElement = document.createElement("div");
	pageObjectElement.innerHTML = pageObject.txBody ? pageObject.txBody.content : "";

	// remove old ellipsis class for the paragraph element, we aren't using this anymore
	const textContainerEl = pageObjectElement.firstElementChild as HTMLElement;
	textContainerEl.classList.remove("csi-ellipsis-nowrap");

	// if we have a filled placeholder, we need to make sure the label and value are
	// split into four spans, the label, disabled, separator, and value spans
	let contentElementSpans = pageObjectElement.querySelectorAll("span");
	if (contentElementSpans && contentElementSpans.length < 4) {
		let firstSpan = contentElementSpans[0];
		if (!firstSpan) {
			firstSpan = document.createElement("span");
			firstSpan.innerText = textContainerEl.innerText;
			const { style } = firstSpan;
			style.fontFamily = textContainerEl.style.fontFamily || "Arial";
			style.fontSize = textContainerEl.style.fontSize || "12px";
			style.color = textContainerEl.style.color || "rgb(0, 0, 0)";
			textContainerEl.innerHTML = "";
			textContainerEl.appendChild(firstSpan);
		}
		const firstSpanParent = firstSpan.parentNode;

		// query and clear the text node without losing style using the TreeWalker API
		const walker = document.createTreeWalker(firstSpan, NodeFilter.SHOW_TEXT);
		const node = walker.nextNode();

		// get the label and value from the node, or just the label if it's not filled
		const { nodeValue } = node;
		const label = nodeValue.split(":")[0] + ":";
		let placeholderValue = "{Value}";
		// check if placeholder is filled based on the text
		if (nodeValue.includes(label)) {
			pageObject.filled = true;
			placeholderValue = nodeValue.replace(label, "")
				.trim();
		}

		// now we can clear the text inside the span to prep for duplication
		node.nodeValue = "";

		// add disabled span with text node and display set to none initially because showLabel default is true
		const disabledEl = document.createElement("span");
		const { style } = disabledEl;
		style.fontSize = "12px";
		style.color = "var(--Grey700)";
		style.display = "none";
		disabledEl.innerHTML = `<em>${label}</em>`;
		firstSpanParent.appendChild(disabledEl);

		// now we need to append the separator node
		const separatorEl = document.createElement("span");
		const separatorTextNode = document.createTextNode(" ");
		separatorEl.appendChild(separatorTextNode);
		firstSpanParent.appendChild(separatorEl);

		// duplicate the first span and appending it to the parent (this will be the value span)
		firstSpanParent.appendChild(firstSpan.cloneNode());

		// give the first span back it's initial text value
		node.nodeValue = label;

		// create new text node to give to last child
		const placeholderTextNode = document.createTextNode(placeholderValue);
		firstSpanParent.lastChild.appendChild(placeholderTextNode);

		// requery spans
		contentElementSpans = pageObjectElement.querySelectorAll("span");

		// give unique ids for use later when giving styles
		contentElementSpans[0].id = PLACEHOLDER_LABEL_SPAN;
		contentElementSpans[1].id = PLACEHOLDER_DISABLED_SPAN;
		contentElementSpans[2].id = PLACEHOLDER_SEPARATOR_SPAN;
		contentElementSpans[3].id = PLACEHOLDER_VALUE_SPAN;

		// this attribute may not exist, make sure it does and is true.
		pageObject.showLabel = true;

		pageObject.txBody.content = pageObjectElement.innerHTML;
	}
};

/**
 * Apply style to text if shape is selected but not in edit mode
 * @param pageObject The target pageObject to apply text styles
 * @param {string} domString
 * @param {string} styleList The styles to be applied
 * @param {boolean} apply Flag to set or unset style
 * @param {boolean} placeholderTextFormat true for value, false for label
 * @returns An object containing content HTML with styles applied and computed text height after style application
 */
export const applyTextStyles = (
	pageObject: IShape, domString: string, styleList: IAnyObjectType, apply: boolean,
	isFormatPainter?: boolean, placeholderTextFormat?: string,
) => {
	const headingTags = ["H1", "H2", "H3", "H4"];
	const contentElement = document.createElement("div") as HTMLElement;

	contentElement.innerHTML = domString;

	// take rich text out, add it back later when all text styling is done
	const richTextDiv = getPlaceholderRichTextElement(contentElement);
	let richTextDivCopy;
	if (richTextDiv) {
		richTextDivCopy = richTextDiv.cloneNode(true);
		richTextDiv.remove();
	}

	// If there is no span tag present inside block elements adding span before applying styles
	const blocksElements = contentElement.querySelectorAll("p, h1, h2, h3, h4, li");
	_.each(blocksElements, (block) => {
		// LI can also contain h1,h2,h3,h4, we need to ignore all such occurrences.
		if (block.nodeName === "LI"
			|| block.parentElement && block.parentElement.nodeName !== "LI") {
			const firstChild = block.firstChild as HTMLElement;
			if (firstChild && firstChild.nodeName === "#text") {
				const span = document.createElement("span");
				span.innerHTML = block.innerHTML;
				block.innerHTML = span.outerHTML;
			}
		}
	});
	const _selectSpans = (contentElement) => {
		if (pageObject.attributeKey) {
			const all = contentElement.querySelectorAll(`#${PLACEHOLDER_LABEL_SPAN}, #${PLACEHOLDER_VALUE_SPAN}`);
			switch (placeholderTextFormat) {
				case PlaceholderTextFormat.LABEL:
					return contentElement.querySelectorAll(`#${PLACEHOLDER_LABEL_SPAN}`);
				case PlaceholderTextFormat.VALUE:
					return contentElement.querySelectorAll(`#${PLACEHOLDER_VALUE_SPAN}`);
				default:
					return all;
			}
		} else {
			return contentElement.querySelectorAll("span");
		}
	};
	// apply tag styles like strong, bold, italic and underline
	_.each(styleList, (value, style) => {
		// apply block level style such as font-family, color and font-size
		const spans = _selectSpans(contentElement);
		switch (style) {
			case "font-family":
				_.each(spans, (span) => {
					const spanElement = span as HTMLElement;
					spanElement.style.fontFamily = value;
				});
				break;
			case "color":
				_.each(spans, (span) => {
					const spanElement = span as HTMLElement;
					spanElement.style.color = value;
				});
				break;
			case "font-size":
				_.each(spans, (span) => {
					const spanElement = span as HTMLElement;
					spanElement.style.fontSize = value;
				});
				break;
			case "font-style":
				_.each(spans, (span) => {
					const spanElement = span as HTMLElement;
					if (apply) {
						spanElement.style.fontStyle = value;
					} else {
						spanElement.style.fontStyle = "normal";
					}
				});
				break;
			case "font-weight":
				_.each(spans, (span) => {
					const spanElement = span as HTMLElement;
					spanElement.style.fontWeight = value;
				});
				break;
			default:
				break;
		}

		if (FROALA_FORMAT_STYLES.indexOf(value) !== -1) {
			const spans = _selectSpans(contentElement);
			if (apply) {
				_.each(spans, (span) => {
					const a = document.createElement(value);
					a.innerHTML = span.innerHTML;
					span.innerHTML = "";
					span.appendChild(a);
				});
				const blocks = contentElement.querySelectorAll("p, h1, h2, h3, h4, li");
				_.each(blocks, (block) => {
					_.each(block.children, (b) => {
						if (b.nodeName !== "SPAN" && headingTags.indexOf(b.nodeName) === -1) {
							const a = document.createElement(value);
							a.innerHTML = b.innerHTML;
							block.replaceChild(a, b);
						}
					});
				});
			} else {
				_.each(spans, (span) => {
					const re = new RegExp(`<\/?${value}>`, "g");
					span.outerHTML = span.outerHTML.replace(re, "");
				});
			}
		}

		// apply list styles
		if (style === "ol" || style === "ul") {
			if (apply) {
				const listElement = document.createElement(style.toLowerCase());
				_.each(contentElement.children, (pChild, index, content) => {
					const li = document.createElement("li");
					if (pChild && pChild.nodeName === "P") {
						li.innerHTML = pChild.innerHTML;
						const pElm = pChild as HTMLElement;
						li.style.textAlign = pElm.style.textAlign;
						listElement.appendChild(li);
					} else if (pChild && headingTags.indexOf(pChild.nodeName) !== -1) {
						li.innerHTML = pChild.outerHTML;
						listElement.appendChild(li);
					} else if (pChild.nodeName === "OL" || pChild.nodeName === "UL") {
						// remove all child inside the current list and add those in new list
						while (pChild.firstChild) {
							listElement.appendChild(pChild.removeChild(pChild.firstChild));
						}
					}
				});
				let marginLeft = null;
				// remove all the content inside the froala
				while (contentElement.firstChild) {
					marginLeft = (contentElement.firstChild as HTMLElement).style.marginLeft;
					contentElement.removeChild(contentElement.firstChild);
				}

				// In froala editor, only margin-left style is being applied using indent/outdent
				// for list. If list type is getting changed then preserve style and then apply
				// style on new list type.
				if (marginLeft) {
					listElement.style.marginLeft = marginLeft;
				}
				// append newly created text editor contents
				contentElement.appendChild(listElement);
			} else {
				const lists = contentElement.querySelectorAll("ol, ul");
				_.eachRight(lists, (list) => {
					_.eachRight(list.children, (liChild) => {
						let element;
						const firstChildNodeName = liChild.firstChild && liChild.firstChild.nodeName;
						// if li has paragraph or heading tag as child then using it as it is to add in div container
						// else creating paragraph element and adding the li content into it.
						if (firstChildNodeName === "P" || headingTags.indexOf(firstChildNodeName as string) > -1) {
							element = liChild.firstChild as HTMLElement;
						} else {
							element = document.createElement("p");
							element.style.textAlign = (liChild as HTMLElement).style.textAlign;
							element.innerHTML = liChild.innerHTML;
						}
						contentElement.insertBefore(element, contentElement.firstChild);
					});
					(list.parentElement as HTMLElement).removeChild(list);
				});
			}
		}

		// set vertical align as needed
		if (style === VERTICALALIGN_KEY) {
			pageObject.spPr.verticalAlign = value;
		}

		// apply text align style
		if (style === TEXTALIGN_KEY) {
			// Find all P, h1, h2, h3 and h4 and apply text align,
			const blocks = contentElement.querySelectorAll("p, h1, h2, h3, h4");
			_.each(blocks, (block) => {
				const blockElement = block as HTMLElement;
				blockElement.style.textAlign = value;
			});
			// Find all li and if they don't have heading style apply text align to li
			const liBlocks = contentElement.querySelectorAll("li");
			_.each(liBlocks, (block) => {
				_.each(block.children, (b) => {
					const liInnerDom = b as HTMLElement;
					if (!liInnerDom.querySelector("h1, h2, h3, h4")) {
						block.style.textAlign = value;
					}
				});
			});
		}
		// apply indent/outdent to text
		if (style === "outdent" || style === "indent") {
			_.each(contentElement.children, (pChild, index, content) => {
				const blockElement = pChild as HTMLElement;
				const originalMargin = parseInt(blockElement.style.marginLeft || "0", 0);
				const finalMargin = (style === "indent") ? originalMargin + INDENT_SIZE :
					(originalMargin - INDENT_SIZE) < 0 ? 0 : (originalMargin - INDENT_SIZE);
				if (finalMargin + MIN_RIGHT_DISTANCE_FOR_INDENT < pageObject.spPr.xfrm.ext.cx) {
					blockElement.style.marginLeft = finalMargin + "px";
				}
			});
		}

		// apply paragraph styles
		if (style === "paragraph" && value) {
			if (value !== "n") {
				// remove font-size before applying paragraph style
				_.each(contentElement.querySelectorAll("*[style]"), ($el: any) => {
					const attr = $el.getAttribute("style") as string;
					if (attr.indexOf("font-size") >= 0) {
						$el.style.fontSize = "";
					}
				});

				const tempElement = document.createElement("div");
				_.each(contentElement.children, (pChild, index, content) => {
					if (pChild && (pChild.nodeName === "P" || headingTags.indexOf(pChild.nodeName) !== -1)) {
						const headingElement = document.createElement(value.toLowerCase());
						headingElement.innerHTML = pChild.innerHTML;
						const pElm = pChild as HTMLElement;
						headingElement.style.textAlign = pElm.style.textAlign;
						tempElement.appendChild(headingElement);
					}
					if (pChild && (pChild.nodeName === "OL" || pChild.nodeName === "UL")) {
						const list = document.createElement(pChild.nodeName.toLowerCase());
						_.each(pChild.children, (liChild) => {
							let liContents = liChild.innerHTML;
							const listElement = document.createElement("li");

							// check if li has heading elements then replace them with given value
							// else create given heading element
							if (liContents.indexOf("<h") > -1) {
								liContents = liContents.replace(/<(h1|h2|h3|h4)/g, "<" + value.toLowerCase());
								liContents = liContents.replace(/<(\/)(h1|h2|h3|h4)/g, "</" + value.toLowerCase());
								listElement.innerHTML = liContents;
							} else {
								const headingElement = document.createElement(value.toLowerCase());
								headingElement.innerHTML = liContents;
								listElement.appendChild(headingElement);
							}

							list.appendChild(listElement);
						});
						tempElement.appendChild(list);
					}
				});
				while (contentElement.firstChild) {
					contentElement.removeChild(contentElement.firstChild);
				}
				contentElement.innerHTML = tempElement.innerHTML;
			} else {
				// replacing all heading tags with p tag
				contentElement.innerHTML = contentElement.innerHTML.replace(/<(\/?)(h1|h2|h3|h4)/g, "<$1p");
				// adding font-size to 12px after removing the paragraph styles
				const spansElements = contentElement.querySelectorAll("span");
				_.each(spansElements, (span) => {
					const spanElement = span as HTMLElement;
					spanElement.style.fontSize = DEFAULT_FONT_SIZE + "px";
				});
			}
		}
		// remove p tags in case of any empty p tag
		_.each(contentElement.querySelectorAll("p"), (p) => {
			const html = p.innerHTML.trim();
			if (!html || html.toLowerCase() === "&nbsp;") {
				const parent = p.parentNode as Node;
				parent.removeChild(p);
			}
		});

		// apply indent at last while format painter after creating all block level elements
		if (styleList["margin-left"] && isFormatPainter) {
			_.each(contentElement.children, (pChild, index, content) => {
				const blockElement = pChild as HTMLElement;
				blockElement.style.marginLeft = styleList["margin-left"];
			});
		}
	});

	if (richTextDivCopy) {
		contentElement.appendChild(richTextDivCopy);
	}

	const textShapeHeight = getShapeFitToTextContent(pageObject, contentElement.innerHTML).height;
	const textShapeWidth = getShapeFitToTextContent(pageObject, contentElement.innerHTML).widestSpan;
	return { innerHTML: contentElement.innerHTML, textShapeHeight, textShapeWidth };
};

/**
 * Get 'shape fit to text' height.
 * Utility method for shapes which are intended to increase in height per its text content.
 * This utility uses a hidden div in document to measure textual content height.
 * @param pageObject The pageObject against which to compute textual height. Its width is used to set base width for
 *      measurement div. Currently method assumes the shape to be text box, however, method is generic enough for
 *      any shape application (assumption is due to the left and right margin applied to text box which is not derived
 *      (actually not present) from model)
 *      See comment in centric/c8-canvas/src/presetShapesMeta/rect.ts for 'body properties'
 * @param contentHtmlString The HTML in string form
 * @returns Text content height
 */
export const getShapeFitToTextContent = (pageObject: IShape, contentHtmlString: string) => {
	// If shape is text box then Get the bounding dimensions of text with applied formatting.
	// Required for text boxes, being fit to containing text.
	// Trick is to use a hidden container in document where we put the contents and then measure the bounding
	// dimensions of it.
	let textMeasureDiv = document.getElementById("text_measure_div");
	const { spPr } = pageObject;
	const autoWidth = spPr.autosize === AutosizeMode.FIXED_HEIGHT;
	const boundingWidth = spPr.xfrm.ext.cx;
	// Initialize, if not available
	if (!textMeasureDiv) {
		textMeasureDiv = document.createElement("div");
		textMeasureDiv.classList.add("fr-element", "fr-view");
		const { style } = textMeasureDiv;
		style.display = "inline-block";
		style.position = "fixed";
		style.top = "250px";
		style.visibility = "hidden"; // set to "" to see in UI for debugging purposes
		textMeasureDiv.id = "text_measure_div";
		document.body.appendChild(textMeasureDiv);
	}

	let widestSpan = 0;
	const { style } = textMeasureDiv;
	if (autoWidth) {
		textMeasureDiv.innerHTML = contentHtmlString;
		style.wordWrap = "normal";
		style.whiteSpace = "nowrap";
	} else {
		// account for padding on each side (20px total - 10px left & 10px right)
		style.width = `${boundingWidth - DEFAULT_TEXTBOX_PADDING}px`;
		textMeasureDiv.innerHTML = contentHtmlString;
		style.wordWrap = "break-word";
		style.whiteSpace = "normal";
		widestSpan = boundingWidth;
	}
	// get dimensions of first element beneath div
	const dim = getTextDimensions(textMeasureDiv);
	const height = dim.height;
	widestSpan = widestSpan || dim.width + DEFAULT_TEXTBOX_PADDING;
	return { height, widestSpan };
};

/**
 * Convert HEX to RGB color format
 * @param hex {string}
 * @return {string}
 */
const hexToRgb = (hex: string): string => {
	// Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
	const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
	hex = hex.replace(shorthandRegex, (m, r, g, b) => {
		return r + r + g + g + b + b;
	});
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result ? "rgb(" + parseInt(result[1], 16) + ", " +
		parseInt(result[2], 16) + ", " + parseInt(result[3], 16) + ")" : "";
};

/**
 * Generate default line fill for lines without a fill (post-migration issue)
 * @return {IFillProperties}
 */
export const getDefaultLineFill = (): ISolidFillProperties => {
	return {
		color: "#000000",
		type: FillTypes.SOLID_FILL,
	};
};

/**
 * Get the shape fill string for shape path.
 * If NO_FILL, then "none" is returned.
 * IF SOLID_FILL, then colour is returned.
 * If PICTURE_FILL, then url pointing to image pattern definition is returned.
 * @param shapeId {string} shape id for which fill is to be applied.
 * @param fill {IFillProperties} fill for the shape.
 * @param imageFillUUID {string} unique id to append to image fill to prevent overlap
 */
export const getShapeFill =
	(
		shapeId: string,
		fill: IFillProperties<FillTypes> | undefined,
		imageFillUUID?: string | undefined,
	): string => {
		let fillString = "none";
		if (!fill) {
			return fillString;
		}

		switch (fill.type) {
			case FillTypes.NO_FILL:
				// Do nothing. fill is already set to none
				break;
			case FillTypes.SOLID_FILL:
				fillString = (fill as ISolidFillProperties).color;
				break;
			case FillTypes.PICTURE_FILL:
				// In case of picture fill, getImageFillDefs() method will create pattern definition.
				// Create fill string to point to the image pattern definition.
				fillString = `url(#${shapeId}_image_fill_${imageFillUUID})`;
				break;
			default:
				// Do nothing. fill is already set to none
				break;
		}
		return fillString;
	};

/**
 * Get whether given value is near to given target. The tolerance is the value which being used to
 * define range to evaluate the nearness with the target. Also, the minimum value of the range is
 * inclusive and maximum value of the range is exclusive.
 * If given value falls in range, then returned true.
 * IF given value falls out of range, then returned false.
 * @param value {number} the value to be checked for nearness.
 * @param target {number} the target value.
 * @param tolerance {number} the value used to calculate range for nearness (+/- tolerance from target)
 *
 * @return {boolean}
 */
export const isNear = (value: number, target: number, tolerance: number): boolean => {
	const minValue = target - tolerance;
	const maxValue = target + tolerance;
	return (value >= minValue && value < maxValue);
};

/**
 *  Remove zero space width(&#8203) from string
 * @param value {string}
 */
export const removeZeroSpaceWidth = (value: string) => {
	return (value || "").replace(/\u200B/g, "");
};

/**
 * Remove new line and trim text
 * @param value {string}
 */
export const removeLineBreak = (value: string) => {
	return (value || "").replace(/\n/ig, "")
		.trim();
};

/**
 * Adjusts given position within the editor boundary. When given position is
 * outside of the editor boundary then it returns position with max/min boundary value.
 *
 * @param point the position needs to be adjust within editor boundary
 * @param editorBoundary the editor's boundary
 * @param drawShape the flag to represent whether drag event is for drawing shape
 * @return { IPosition }
 */
export const adjustPositionWithinEditor = (
	point: IPosition, editorBoundary: IRectSizeBox,
	drawShape: boolean, noZoom?: boolean,
): IPosition => {
	let pointX = point.x;
	let pointY = point.y;
	let { width, height } = editorBoundary;
	if (!noZoom) {
		// consider zoom factor for editor width and height
		width = getZoomValue(width);
		height = getZoomValue(height);
	}
	if (drawShape) {
		if (pointX < 0) {
			pointX = 0;
		} else if (pointX > width) {
			pointX = width;
		}

		if (pointY < 0) {
			pointY = 0;
		} else if (pointY > height) {
			pointY = height;
		}
	} else {
		if (pointX < editorBoundary.left) {
			pointX = editorBoundary.left;
		} else if (pointX > editorBoundary.right) {
			pointX = editorBoundary.right;
		}

		if (pointY < editorBoundary.top) {
			pointY = editorBoundary.top;
		} else if (pointY > editorBoundary.bottom) {
			pointY = editorBoundary.bottom;
		}
	}

	return {
		x: pointX,
		y: pointY,
	};
};

/**
 * Offset root position using querySelector on canvasContainer for optimal DOM search
 * @param canvasContainerEl canvas container html element
 * @param position - initial position
 * @param offset - offset used for position
 */
export const adjustPositionForRoot = (canvasContainerEl: HTMLElement, position: IPosition, offset?: IPosition): void => {
	const canvasContainerBound = canvasContainerEl.getBoundingClientRect();
	const canvasRootBound = canvasContainerEl.querySelector(CANVAS_EDITOR_ROOT)
		.getBoundingClientRect();
	const paddingLeft = getZoomValue(canvasRootBound.left - canvasContainerBound.left);
	const paddingTop = getZoomValue(canvasRootBound.top - canvasContainerBound.top);

	if (offset) {
		position.x = offset.x - paddingLeft;
		position.y = offset.y - paddingTop;
	} else {
		position.x -= paddingLeft;
		position.y -= paddingTop;
	}
};

/**
 * Return delta for modified curve during edit curve operation
 * @param pageObject {IShape | IConnectionShap}
 * @return { shapePathLst, deltaOffX, deltaOffY, widthDiffPer, heightDiffPer, shapePathDelta }
 */
export const getModifiedCurveDelta = (pageObject: IShape | IConnectionShape) => {
	const { spPr } = pageObject as IShape;
	const { ext, off } = spPr.xfrm;
	const { cx, cy } = ext;
	const geometry = spPr.geometry as ICustomGeometry;
	const penGeometry = spPr.geometry as IPenGeometry;

	// generate geometry params using the shape geometry.
	const geometryParams = getCustomGeometry({
		cy, cx, geometry,
		shapeAvList: undefined,
	});

	// Get dimensions for edited shape.
	const dimensions = Snap.path.getBBox(geometryParams.pathInfo.pathString);
	const { width, height } = dimensions;
	const dx = dimensions.x;
	const dy = dimensions.y;
	let shapeCenter = getShapeCenter(pageObject);

	// remove offset from the center, make it local to shape dimensions.
	shapeCenter = {
		x: shapeCenter.x - off.x,
		y: shapeCenter.y - off.y,
	};

	// Find center for the updated shape, again this is relative to shape's dimensions.
	const newShapeCenter = {
		x: (dimensions.x + dimensions.width / 2),
		y: (dimensions.y + dimensions.height / 2),
	};

	// Find delta for the center change.
	const centerDiff = {
		x: newShapeCenter.x - shapeCenter.x,
		y: newShapeCenter.y - shapeCenter.y,
	};

	// let's apply shape transformations to the center delta
	const mappedCenterDelta = mapPointToShapeMatrix(pageObject, centerDiff, false);

	// using shape center and delta, find the absolute position for the new center.
	const newCenter = {
		x: off.x + shapeCenter.x + mappedCenterDelta.x,
		y: off.y + shapeCenter.y + mappedCenterDelta.y,
	};

	// find out offset using new center.
	const newOff = {
		x: newCenter.x - width / 2,
		y: newCenter.y - height / 2,
	};

	// Shape offsets should be updated using this delta
	const offsetDelta = {
		x: newOff.x - off.x,
		y: newOff.y - off.y,
	};

	// Delta for height & width.
	const heightDiff = height - cy;
	const widthDiff = width - cx;

	// calculate below parameter in ratio of ext.cx or ext.cy.
	// use MIN_DIMENSION if and only if ext.cx or ext.cy is zero.
	const deltaOffX = offsetDelta.x / (cx || MIN_DIMENSION);
	const deltaOffY = offsetDelta.y / (cy || MIN_DIMENSION);
	const widthDiffPer = widthDiff / (cx || MIN_DIMENSION);
	const heightDiffPer = heightDiff / (cy || MIN_DIMENSION);

	// don't modify list in geometry
	const shapePathLst = _.cloneDeep(geometry.pathLst);

	// scale path list for rendering and apply offsets.
	// we want to apply offsets in reverse, since points are already at the position
	// and we will be updating shape offsets as well.
	updateShapePathList(shapePathLst, -dx, -dy);
	let handlePoints = undefined;

	if (penGeometry.handlePoints) {
		handlePoints = _.cloneDeep(penGeometry.handlePoints);

		_.each(handlePoints, (handlePoint) => {
			if (handlePoint.frontHandle) {
				handlePoint.frontHandle.x -= dx;
				handlePoint.frontHandle.y -= dy;
			}
			if (handlePoint.backHandle) {
				handlePoint.backHandle.x -= dx;
				handlePoint.backHandle.y -= dy;
			}
		});
	}

	// Delta by which shape path is changed, we will be using this value for crop shape manipulation.
	const shapePathDelta = {
		x: dx,
		y: dy,
	};
	return { shapePathLst, deltaOffX, deltaOffY, widthDiffPer, heightDiffPer, shapePathDelta, handlePoints };
};

/**
 * Build Custom shape point array using shape geometry. All edit curve operation will be done on this array.
 */
export const buildShapePointList = (geometry: ICustomGeometry | IPenGeometry) => {
	const shapePathLst = geometry.pathLst;
	const shapePath = shapePathLst[0];

	// regenerate path list, we will use endpoints from modified path list
	const points = _.map(shapePath.operations, (pathOperation: PathOperation) => {
		return {
			isCurvePoint: pathOperation.type === PathOperationTypes.LINE_TO,
			position: getPathEndPoint(pathOperation),
		} as ICustomShapePoint;
	});
	if (geometry.type === GeometryTypes.PEN) {
		if (!points[points.length - 1].position) {
			points.pop();
		}
		if (points[0].position.x == points[points.length - 1].position.x &&
			points[0].position.y == points[points.length - 1].position.y) {
			// if curve is closed, do not include final point twice
			points.pop();
		}
	}
	return points;
};

/**
 * Returns the correct point to select when you start editing a shape (the last point added, not including a close operation).
 * @param {IShape} pageObject
 */
export const getCurveSelectionPoint = (pageObject: IShape) => {
	let pointIndex = 0;
	const shapePath = pageObject.spPr.geometry.pathLst;
	if (shapePath && shapePath.length) {
		const operations = shapePath[0].operations;
		const pointCounts = operations.length;
		if (operations[pointCounts - 1].type === PathOperationTypes.CLOSE) {
			pointIndex = pointCounts - 2;
		} else {
			pointIndex = pointCounts - 1;
		}
	}
	return pointIndex;
};

/**
 * Return rich text element from inside placeholder element
 */
export const getPlaceholderRichTextElement = (placeholderElement: Element): Element | undefined => {
	return placeholderElement?.querySelector(`#${PLACEHOLDER_RICH_TEXT_DIV}`);
};

/**
 * Algorithm for slicing a bezier curve given the relevant points, control points, and fraction of the length
 * at which to slice.
 */
export const sliceBezier = (startPoint: IPosition, controlPoint1: IPosition, controlPoint2: IPosition, endPoint: IPosition, t: number) => {
	const x1 = startPoint.x;
	const y1 = startPoint.y;
	const x2 = controlPoint1.x;
	const y2 = controlPoint1.y;
	const x3 = controlPoint2.x;
	const y3 = controlPoint2.y;
	const x4 = endPoint.x;
	const y4 = endPoint.y;

	const x12 = (x2 - x1) * t + x1;
	const y12 = (y2 - y1) * t + y1;

	const x23 = (x3 - x2) * t + x2;
	const y23 = (y3 - y2) * t + y2;

	const x34 = (x4 - x3) * t + x3;
	const y34 = (y4 - y3) * t + y3;

	const x123 = (x23 - x12) * t + x12;
	const y123 = (y23 - y12) * t + y12;

	const x234 = (x34 - x23) * t + x23;
	const y234 = (y34 - y23) * t + y23;

	const x1234 = (x234 - x123) * t + x123;
	const y1234 = (y234 - y123) * t + y123;

	const firstHandle: IPosition = { x: x12, y: y12 };
	const secondHandle: IPosition = { x: x123, y: y123 };
	const destination: IPosition = { x: x1234, y: y1234 };

	return { firstHandle, secondHandle, destination };
};

/**
 * Return mapped point relative to curve.
 * @param pageObject {IShape | IConnectionShape}
 * @param position {IPosition} point which we want to mapped
 * @param parentSize {TopLeft & BottomRight} Offset of the canvas-editor root
 */
export const getMappedCurvePoint = (
	pageObject: IShape | IConnectionShape,
	position: IPosition, parentSize: TopLeft & BottomRight, groupObject: IGroupShape | undefined,
) => {
	if (groupObject) {
		// If we are getting a point within a group, we use the group as a new "page" and calculate
		// within that framework
		const groupSize = _.cloneDeep(parentSize);
		groupSize.left = 0;
		groupSize.top = 0;
		groupSize.bottom = groupObject.spPr.xfrm.ext.cy;
		groupSize.right = groupObject.spPr.xfrm.ext.cx;

		position = getMappedCurvePoint(groupObject as any as IShape, position, parentSize, undefined);
		parentSize = groupSize;
	}

	// center is with respect to screen, later we will adjust it to the shape dimensions.
	const center = getShapeCenter(pageObject);

	// get the point with respect to center.
	const x = getZoomValue(position.x - parentSize.left) - center.x;
	const y = getZoomValue(position.y - parentSize.top) - center.y;

	// map point as per rotation and scale applied.
	const mappedLocation = mapPointToShapeMatrix(pageObject, { x, y });

	const { spPr } = pageObject as IShape;

	// adjust center to shape dimensions.
	center.x = center.x - spPr.xfrm.off.x;
	center.y = center.y - spPr.xfrm.off.y;

	// find the new point in shape's co-ordinate system.
	mappedLocation.x += center.x;
	mappedLocation.y += center.y;
	return mappedLocation;
};

/**
 * Return shape ids for shape those are migrated from 6.2 and have migrationExtents property in txBody
 * @param pageObjects {IAnyObjectType[]}
 * @return {string[]}
 */
export const getMigratedShapeIds = (pageObjects: IAnyObjectType[]): string[] => {
	return pageObjects.filter((shape) => {
		return (shape.type === IPageObjectTypes.SHAPE && shape.txBody && shape.txBody.migrationExtents);
	})
		.map((shape) => { return shape.shapeId; });
};

// C8-10199 Too many undo actions created when dragging group in canvas
// Enables detecting if user is dragging object. Necessary because end up
// with too many undo operations for one drag operation.
let isDragging: Boolean = false;

// Used to reset if for some reason isDragging is not set
// to false within reasonable period of time. Avoid situations
// where isDragging is not reset at end of a drag operation,
// and then subsequent undo operations not created properly.
let lastDragTime: number = 0;

// Enables checking if user is dragging, but also used to
// reset isDragging if lastDragTime is unreasonable.
export const userIsDragging = (): Boolean => {
	// No one drags for more than 30 seconds?
	if ((lastDragTime - new Date()
		.getTime()) > 30000) {
		// For some reason lastDragTime was not reset so reset it
		// and also the last drag time.
		isDragging = false;
		lastDragTime = 0;
	}
	return isDragging;
};

/**
 * Apply autosize extents to individual shapes
 * @param pageObject - shape to apply autosize
 * @param {boolean} resizeForContent - flag to resize extents for content
 */
export const applyAutosizeExtents = (pageObject: IShape, resizeForContent?: boolean) => {
	const { txBody } = pageObject;
	if (txBody && txBody.content) {
		const content = getShapeFitToTextContent(pageObject, txBody.content);
		const { spPr } = pageObject;
		const { ext } = spPr.xfrm;
		if (spPr.autosize === AutosizeMode.FIXED_HEIGHT || resizeForContent) {
			ext.cx = content.widestSpan;
		}

		if (spPr.autosize === AutosizeMode.FIXED_WIDTH || resizeForContent) {
			const offset = (pageObject.attributeKey) ? PADDING_INSIDE_ATTRIBUTE_PLACEHOLDER_SHAPES : EXT_OFFSET;
			ext.cy = content.height + offset;
		}
	}
};

/**
 * Run through the placeholder and reset height based on contents
 * @param groupObject - the placeholder object we are resetting
 */
export const resetPlaceholderGroup = (groupObject: IGroupShape) => {
	const { autospace } = groupObject;
	// Start with the top position and run down the entire placeholder until we reach the end.
	let OFF_Y = groupObject.childObjects[0].spPr.xfrm.off.y;
	groupObject.childObjects.forEach((childObject: any) => {
		const { spPr } = childObject;
		const { ext, off } = spPr.xfrm;
		if (autospace) {
			off.x = 0; // reset child x to where the group is
			off.y = OFF_Y;
		}
		if (!childObject.imagePlaceholder &&
            !childObject.isPicture &&
            spPr.autosize !== AutosizeMode.FIXED_SIZE) {
			const content = getShapeFitToTextContent(childObject, childObject.txBody.content);
			if (spPr.autosize === AutosizeMode.FIXED_HEIGHT) {
				ext.cx = content.widestSpan;
			} else if (spPr.autosize === AutosizeMode.FIXED_WIDTH) {
				ext.cy = content.height + PADDING_INSIDE_ATTRIBUTE_PLACEHOLDER_SHAPES;
			}
		}
		OFF_Y += (childObject.spPr.xfrm.ext.cy + SPACE_BETWEEN_ATTRIBUTE_PLACEHOLDER_SHAPES);
	});
	calculateAdjustGroup(groupObject);
};

/**
 * Run through the placeholder and reset height based on contents
 * @param groupObject - the placeholder object we are resetting
 */
export const togglePlaceholderLabel = (childObject: IShape, showLabel: boolean) => {
	const pageObjectElement = document.createElement("div") as HTMLElement;
	pageObjectElement.innerHTML = childObject.txBody.content;
	const labelEl = pageObjectElement.querySelector("#" + PLACEHOLDER_LABEL_SPAN) as HTMLElement;
	const disabledEl = pageObjectElement.querySelector("#" + PLACEHOLDER_DISABLED_SPAN) as HTMLElement;
	const valueEl = pageObjectElement.querySelector("#" + PLACEHOLDER_VALUE_SPAN) as HTMLElement;
	const richTextDiv = getPlaceholderRichTextElement(pageObjectElement);
	if (showLabel) {
		childObject.showLabel = true;
		html.setVisible(labelEl, true);
		html.setVisible(disabledEl, false);
	} else {
		childObject.showLabel = false;
		html.setVisible(labelEl, false);
		html.setVisible(disabledEl, (!childObject.filled || (!valueEl.innerText.length && !richTextDiv?.firstChild)));
	}
	childObject.txBody.content = pageObjectElement.innerHTML;
};

/**
 * Run through the placeholder and optionally show the label based on editMode and value content.
 */
export const togglePlaceholderLabelOptionally = (childObject: IShape, editMode: boolean) => {
	const pageObjectElement = document.createElement("div") as HTMLElement;
	pageObjectElement.innerHTML = childObject.txBody.content;
	const disabledEl = pageObjectElement.querySelector("#" + PLACEHOLDER_DISABLED_SPAN) as HTMLElement;
	const valueEl = pageObjectElement.querySelector("#" + PLACEHOLDER_VALUE_SPAN) as HTMLElement;
	const richTextDiv = getPlaceholderRichTextElement(pageObjectElement);
	const showDisabledEl = !childObject.showLabel && (!childObject.filled || (editMode && !valueEl.innerText.length && !richTextDiv?.firstChild));
	html.setVisible(disabledEl, showDisabledEl);
	childObject.txBody.content = pageObjectElement.innerHTML;
};

// Enables checking if user is dragging object. If not dragging
// reset the variable we use to detect last drag time.
export const setUserIsDragging = (dragging: Boolean) => {
	isDragging = dragging;
	// If user is not dragging, reset time check.
	lastDragTime = dragging ? new Date()
		.getTime() : 0;
};

/**
 * Remove selection CSS classes from all froala table elements.
 */
export const deselectAllTableElements = () => {
	$(".svgCanvas .fr-element.fr-view table .fr-selected-cell")
		.removeClass("fr-selected-cell");
};
